import React, { createContext, useContext, useMemo, useReducer, useRef } from "react";
import { v4 } from "uuid";
import { debounce } from "lodash";
import { ApolloError } from "@apollo/client/errors";
import {
  ConsultantOpportunityCardFragment,
  KanbanBoardType,
  useCardAddedSubscription,
  useCardDeletedSubscription,
  useCardMovedSubscription,
  useGetConsultantOpportunityBoardQuery,
  useGetConsultantOpportunityCardsLazyQuery,
  useDeleteConsultantOpportunityCardMutation,
  useMoveCardMutation
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { OpportunityBoardConfig } from "@bonliva-crm/shared";
import { OkCancelDialogOptions } from "@containers/ConfirmDialog";
import { useConfirmDialog, useSnackbars, useUserProfile } from "@hooks";
import { OnMoveCardParams } from "../kanban-board/kanban-types";
import {
  BoardAction,
  BoardState,
  consultantOpportunityBoardReducer
} from "./consultant-opportunity-board-reducer";

export type ConsultantOpportunityBoardFilter = {
  text: string | null;
  showOld: boolean;
  showThisMonth: boolean;
};

interface IConsultantOpportunityBoardContext {
  state: BoardState;
  loading: boolean;
  error?: ApolloError;

  refetch: () => void;
  setBoardUserId: (userId: string) => void;
  handleTextSearch: (text: string) => void;
  handleShowOld: (value: boolean) => void;
  handleResponseThisMonth: (value: boolean) => void;
  onMoveCard: (params: OnMoveCardParams) => Promise<void>;
  onDeleteCard: (cardId: string) => Promise<void>;
  canDeleteCard: (cardId: string) => boolean;

  dispatch: React.Dispatch<BoardAction>;
}

const defaultFilter: ConsultantOpportunityBoardFilter = {
  text: null,
  showOld: false,
  showThisMonth: false
};

const ConsultantOpportunityBoardContext =
  createContext<IConsultantOpportunityBoardContext | null>(null);

export const useConsultantOpportunityBoardContext = () => {
  const context = useContext(ConsultantOpportunityBoardContext);
  if (context === null) {
    throw new Error(
      "useConsultantOpportunityBoardContext must be used within ConsultantOpportunityBoardContext"
    );
  }
  return context;
};

export const ConsultantOpportunityBoardContextProvider = ({ children }) => {
  const { showError, showSuccess } = useSnackbars();
  const user = useUserProfile();
  const sessionId = useRef(v4()).current;
  const config = useRef(new OpportunityBoardConfig()).current;
  const confirm = useConfirmDialog();

  const [state, dispatch] = useReducer(consultantOpportunityBoardReducer, {
    board: null,
    initialBoard: null,
    cardIdColumnDataDictionary: {},
    lastActionType: null,
    updateOpportunityIssueId: null,
    finalizePresentationIssueId: null,
    finalizePresentationType: null,
    filters: defaultFilter,
    boardUserId: user.id,
    untagConsultantId: null
  });

  const [loadCards] = useGetConsultantOpportunityCardsLazyQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      boardId: state.boardUserId
    },
    onCompleted: (response) => {
      dispatch({
        type: "LOAD_CARDS_ACTION",
        payload: {
          cards: response.kanbanCards as ConsultantOpportunityCardFragment[]
        }
      });
    }
  });

  // Loading data of board
  const { error, loading, refetch } = useGetConsultantOpportunityBoardQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      ownerId: state.boardUserId
    },
    onCompleted: (response) => {
      dispatch({
        type: state?.board ? "REFETCH_BOARD" : "INITIALIZE_BOARD",
        payload: { response }
      });
      loadCards();
    }
  });

  // Move card callback
  const [_moveCard] = useMoveCardMutation();
  const [_deleteCard] = useDeleteConsultantOpportunityCardMutation();

  const onMoveCard = async (params: OnMoveCardParams) => {
    if (!config.isTransitionAllowed(params.sourceColumnName, params.destColumnName)) {
      return;
    }

    if (params.sourceColumnName === "Presented") {
      dispatch({
        type: "FINALIZE_PRESENTATION_ACTION",
        payload: {
          issueId: params.issueId,
          type: params.destColumnName === "Won" ? "won" : "lost"
        }
      });

      return;
    }

    _moveCard({
      variables: {
        boardId: state?.board?.id,
        boardType: KanbanBoardType.ConsultantOpportunity,
        cardId: params.cardId,
        columnId: params.destColumnId,
        beforeCardId: params.destBeforeCardId,
        sessionId
      }
    }).catch(() => {
      showError("Failed to move card");
      dispatch({
        type: "MOVE_CARD",
        payload: {
          cardId: params.cardId,
          destColumnId: params.sourceColumnId,
          destPosition: params.sourcePosition
        }
      });
    });

    dispatch({
      type: "MOVE_CARD",
      payload: {
        cardId: params.cardId,
        destColumnId: params.destColumnId,
        destPosition: params.destPosition
      }
    });
  };

  const onDeleteCard = async (cardId: string) => {
    confirm.confirm(new OkCancelDialogOptions()).then((v) => {
      if (v !== "ok") {
        return;
      }

      _deleteCard({
        variables: {
          issueId: cardId
        }
      }).then(() => {
        showSuccess(locale.Deleted);

        dispatch({
          type: "DELETE_CARD",
          payload: {
            cardId: cardId
          }
        });
      });
    });
  };

  const debouncedRefetch = useMemo(
    () => debounce(refetch, 2000, { leading: true, trailing: false }),
    [refetch]
  );

  // Subscription to get new card added
  useCardAddedSubscription({
    variables: { boardId: state?.board?.id ?? "", sessionId },
    skip: !state?.board,
    onSubscriptionData: () => {
      debouncedRefetch();
    }
  });

  // Subscription to get board updates and keep data fresh
  useCardMovedSubscription({
    variables: {
      sessionId,
      boardId: state?.board?.id ?? ""
    },
    skip: !state?.board,
    onSubscriptionData: () => {
      debouncedRefetch();
    }
  });

  // Subscription to get card deleted
  useCardDeletedSubscription({
    variables: { boardId: state?.board?.id ?? "", sessionId },
    skip: !state?.board,
    onSubscriptionData: () => {
      debouncedRefetch();
    }
  });

  const handleChangeBoardUser = (userId: string) => {
    dispatch({
      type: "CHANGE_BOARD_USER",
      payload: {
        filter: defaultFilter,
        userId: userId
      }
    });
  };

  const handleTextSearch = (query: string) => {
    // Ignore string with single char
    dispatch({
      type: "FILTER_CHANGE",
      payload: {
        filter: { ...state.filters, text: query.length !== 1 ? query : null }
      }
    });
  };

  const handleShowOld = (value: boolean) => {
    dispatch({
      type: "FILTER_CHANGE",
      payload: {
        filter: { ...state.filters, showOld: value }
      }
    });
  };

  const handleResponseThisMonth = (value: boolean) => {
    dispatch({
      type: "FILTER_CHANGE",
      payload: {
        filter: { ...state.filters, showThisMonth: value }
      }
    });
  };

  const canDeleteCard = (cardId: string) => {
    const column = state.cardIdColumnDataDictionary[cardId];
    return column.title === "Presented";
  };

  return (
    <ConsultantOpportunityBoardContext.Provider
      value={{
        state,
        loading,
        error,
        refetch,
        setBoardUserId: handleChangeBoardUser,
        handleTextSearch,
        handleShowOld,
        handleResponseThisMonth,
        onMoveCard,
        onDeleteCard,
        dispatch,
        canDeleteCard
      }}
    >
      {children}
    </ConsultantOpportunityBoardContext.Provider>
  );
};
